import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propositionsService } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { CheckCircle, AlertCircle, Calendar, Send, User, Mail, FileText } from 'lucide-react';

const PropositionForm = () => {
  const { sessionToken } = useParams();
  const navigate = useNavigate();
  
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [selectedMember, setSelectedMember] = useState('');
  const [propositions, setPropositions] = useState([{ titre: '', details: '' }]);
  const [submittedPropositions, setSubmittedPropositions] = useState([]);

  useEffect(() => {
    loadSessionInfo();
  }, [sessionToken]);

  const loadSessionInfo = async () => {
    try {
      setLoading(true);
      const session = await propositionsService.getSessionInfo(sessionToken);
      setSessionInfo(session);
      setError(null);
    } catch (error) {
      console.error('Error loading session:', error);
      if (error.response?.status === 404) {
        setError('Session non trouvée ou inactive');
      } else {
        setError('Erreur lors du chargement de la session');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedMember) {
      setError('Veuillez sélectionner un membre');
      return;
    }
    
    const validPropositions = propositions.filter(p => p.titre.trim());
    if (validPropositions.length === 0) {
      setError('Veuillez saisir au moins une proposition');
      return;
    }

    const selectedMemberData = sessionInfo.members.find(m => m.id.toString() === selectedMember);
    if (!selectedMemberData) {
      setError('Membre sélectionné introuvable');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      // Préparer les données pour l'envoi groupé
      const multiplePropositionsData = {
        nom_membre: selectedMemberData.name,
        email_membre: selectedMemberData.email,
        propositions: validPropositions.map(p => ({ 
          titre: p.titre.trim(),
          details: p.details?.trim() || ''
        }))
      };
      
      // Soumettre toutes les propositions en une fois
      const result = await propositionsService.submitMultiplePropositions(sessionToken, multiplePropositionsData);
      
      // Préparer les données pour l'affichage de confirmation
      const submittedProps = validPropositions.map((prop, index) => ({
        nom_membre: selectedMemberData.name,
        email_membre: selectedMemberData.email,
        titre: prop.titre.trim(),
        id: result.proposition_ids?.[index] || index
      }));
      
      setSubmittedPropositions(submittedProps);
      setSubmitted(true);
      
    } catch (error) {
      console.error('Error submitting propositions:', error);
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Erreur lors de la soumission de vos propositions');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleTitreChange = (index, value) => {
    const newPropositions = [...propositions];
    newPropositions[index].titre = value;
    setPropositions(newPropositions);
    
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleDetailsChange = (index, value) => {
    const newPropositions = [...propositions];
    newPropositions[index].details = value;
    setPropositions(newPropositions);
  };

  const addProposition = () => {
    setPropositions([...propositions, { titre: '', details: '' }]);
  };

  const removeProposition = (index) => {
    if (propositions.length > 1) {
      const newPropositions = propositions.filter((_, i) => i !== index);
      setPropositions(newPropositions);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !sessionInfo) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Session introuvable</h2>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                >
                  Retour à l'accueil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    const selectedMemberData = sessionInfo.members.find(m => m.id.toString() === selectedMember);
    
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {submittedPropositions.length === 1 ? 'Proposition envoyée !' : 'Propositions envoyées !'}
                </h2>
                <p className="text-gray-600 mb-2">
                  Merci <strong>{selectedMemberData?.name}</strong> pour {submittedPropositions.length === 1 ? 'votre proposition' : 'vos propositions'}.
                </p>
                <p className="text-gray-600 mb-6">
                  {submittedPropositions.length === 1 ? 'Elle sera' : 'Elles seront'} automatiquement ajoutée{submittedPropositions.length === 1 ? '' : 's'} à l'ordre du jour du comité du{' '}
                  <strong>{formatDate(sessionInfo.date_comite_cible)}</strong>.
                </p>
                
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                  <h3 className="font-medium text-blue-900 mb-2">
                    {submittedPropositions.length === 1 ? 'Votre proposition' : 'Vos propositions'} :
                  </h3>
                  {submittedPropositions.map((prop, index) => (
                    <div key={index} className="mb-3 last:mb-0 pb-3 last:pb-0 border-b border-blue-200 last:border-b-0">
                      <p className="text-blue-800 font-medium mb-1">"{prop.titre}"</p>
                      {prop.details && prop.details.trim() && (
                        <p className="text-blue-700 text-sm italic ml-2">
                          Détails : {prop.details}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="text-sm text-gray-500 space-y-1">
                  <p>✅ Un email de confirmation a été envoyé à {selectedMemberData?.email}</p>
                  <p>📋 Vous recevrez le procès-verbal après la réunion</p>
                </div>

                <div className="mt-8">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setSubmitted(false);
                      setSelectedMember('');
                      setPropositions([{ titre: '', details: '' }]);
                      setSubmittedPropositions([]);
                    }}
                  >
                    Soumettre d'autres propositions
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Proposition pour l'ordre du jour</CardTitle>
            <CardDescription className="text-lg">
              <strong>{sessionInfo.club_name}</strong><br />
              Comité du {formatDate(sessionInfo.date_comite_cible)}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
              <h3 className="font-medium text-blue-900 mb-2">💡 Comment ça marche :</h3>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• Proposez un sujet que vous aimeriez aborder lors du prochain comité</li>
                <li>• Votre proposition sera automatiquement ajoutée à l'ordre du jour</li>
                <li>• Vous recevrez une confirmation par email</li>
                <li>• Le procès-verbal vous sera envoyé après la réunion</li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                    <p className="text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* Sélection du membre */}
              <div className="space-y-2">
                <label className="flex items-center text-sm font-medium text-gray-700">
                  <User className="w-4 h-4 mr-2" />
                  Membre du comité <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  value={selectedMember}
                  onChange={(e) => {
                    setSelectedMember(e.target.value);
                    if (error) setError(null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">-- Sélectionnez un membre --</option>
                  {sessionInfo.members?.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name} ({member.email})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  Votre email sera automatiquement utilisé pour la confirmation
                </p>
              </div>

              {/* Propositions multiples */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <FileText className="w-4 h-4 mr-2" />
                    Vos propositions <span className="text-red-500 ml-1">*</span>
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addProposition}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    + Ajouter une proposition
                  </Button>
                </div>

                {propositions.map((proposition, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Proposition {index + 1}
                      </span>
                      {propositions.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeProposition(index)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Supprimer
                        </Button>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Titre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={proposition.titre}
                        onChange={(e) => handleTitreChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Titre de la proposition (ex: Révision des tarifs d'adhésion)`}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Détails <span className="text-gray-400">(optionnel)</span>
                      </label>
                      <textarea
                        value={proposition.details}
                        onChange={(e) => handleDetailsChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Précisions, contexte, arguments... (optionnel)"
                        rows={3}
                      />
                      <p className="text-xs text-gray-500">
                        Ces détails apparaîtront dans les remarques du PV pour aider le comité dans sa discussion
                      </p>
                    </div>
                  </div>
                ))}
                
                <p className="text-xs text-gray-500">
                  💡 Vous pouvez ajouter plusieurs propositions avant de les envoyer toutes en une fois
                </p>
              </div>

              {/* Submit button */}
              <div className="pt-4">
                <Button 
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {submitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Envoi en cours...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer {propositions.filter(p => p.titre.trim()).length > 1 ? 'mes propositions' : 'ma proposition'}
                    </div>
                  )}
                </Button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-xs text-gray-500">
                Des questions ? Contactez {sessionInfo.club_name} directement
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PropositionForm;